import React, { useState } from 'react';
import { useAuth } from '@/contexts';
import { setAuthPassword, syncUserWithBackend } from '@/api/auth';
import { loginUser } from '@/lib/cognito';
import {
  clearAuthSession,
  isAccountLinkRequiredError,
  isAuthPasswordError,
  isEmailUnverifiedError,
  writeAuthSession,
} from '@/lib/accountLinking';
import { trackSignInPasswordAdded } from '@/lib/accountLinkAnalytics';
import { decodeIdToken } from '@/utils/tokenUtils';
import { getPasswordValidationError, COGNITO_INVALID_PASSWORD_MESSAGE } from '@/utils/passwordValidation';
import { error } from '@/utils/logger';
import SignInMethodsHelpLink from '@/components/AccountLinking/SignInMethodsHelpLink';
import PasswordField from './PasswordField';
import { SUPPORT_EMAIL } from './accountSettingsCopy';
import {
  ERROR_TEXT_CLASS,
  HINT_CLASS,
  PRIMARY_BUTTON_CLASS,
  STEP_TITLE_CLASS,
  TEXT_BUTTON_CLASS,
} from './accountSettingsClasses';

export type SetPasswordOutcome = 'set' | 'already_set' | 'signed_out';

interface SetPasswordStepProps {
  signInEmail: string | null;
  onCancel: () => void;
  onFinished: (outcome: SetPasswordOutcome) => void;
  onSignInMethodsHelpNavigate?: () => void;
}

export function setPasswordErrorMessage(err: unknown): string {
  if (isAccountLinkRequiredError(err)) {
    return `This email already has a separate Globe Thrivers account. Contact ${SUPPORT_EMAIL} to merge them.`;
  }
  if (isEmailUnverifiedError(err)) {
    return 'Verify your email first, then try again.';
  }
  if (isAuthPasswordError(err) && err.code === 'PASSWORD_POLICY') {
    return COGNITO_INVALID_PASSWORD_MESSAGE;
  }
  return 'Could not set a password. Try again.';
}

const SetPasswordStep: React.FC<SetPasswordStepProps> = ({
  signInEmail,
  onCancel,
  onFinished,
  onSignInMethodsHelpNavigate,
}) => {
  const { login } = useAuth();
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [formError, setFormError] = useState('');
  const [showMergeFaqLink, setShowMergeFaqLink] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const handleSubmit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    if (!signInEmail) {
      setFormError('Your email is missing from this session. Sign in again.');
      return;
    }
    const validationError = getPasswordValidationError(password);
    if (validationError) {
      setFormError(validationError);
      return;
    }
    if (password !== confirmation) {
      setFormError('Passwords do not match.');
      return;
    }

    setIsSaving(true);
    setFormError('');
    setShowMergeFaqLink(false);
    let result: { reauthRequired: boolean };
    try {
      result = await setAuthPassword(password);
    } catch (err) {
      if (isAuthPasswordError(err) && err.code === 'ALREADY_HAS_PASSWORD') {
        onFinished('already_set');
        return;
      }
      if (!isAccountLinkRequiredError(err) && !isAuthPasswordError(err) && !isEmailUnverifiedError(err)) {
        error('[SetPasswordStep] Failed to set password', err);
      }
      setShowMergeFaqLink(isAccountLinkRequiredError(err));
      setFormError(setPasswordErrorMessage(err));
      setIsSaving(false);
      return;
    }

    trackSignInPasswordAdded();
    if (!result.reauthRequired) {
      onFinished('set');
      return;
    }

    clearAuthSession();
    try {
      const session = await loginUser(signInEmail, password);
      writeAuthSession(session);
      const decoded = decodeIdToken(session.idToken);
      if (decoded.email && decoded.cognitoUsername) {
        login(await syncUserWithBackend(decoded.email, decoded.cognitoUsername));
      }
      onFinished('set');
    } catch (err) {
      error('[SetPasswordStep] Sign-in after setting password failed', err);
      onFinished('signed_out');
    }
  };

  return (
    <form className="flex w-full flex-col gap-4" onSubmit={handleSubmit} noValidate>
      <h3 className={STEP_TITLE_CLASS}>Set a password</h3>
      <div className="flex flex-col gap-3">
        <PasswordField
          label="New password"
          value={password}
          onChange={setPassword}
          autoComplete="new-password"
          disabled={isSaving}
        />
        <PasswordField
          label="Confirm password"
          value={confirmation}
          onChange={setConfirmation}
          autoComplete="new-password"
          disabled={isSaving}
        />
      </div>
      <p className={HINT_CLASS}>
        At least 8 characters, with upper and lowercase letters, a number, and a symbol.
      </p>
      {formError && (
        <div className="flex flex-col gap-1.5">
          <p className={ERROR_TEXT_CLASS} role="alert">{formError}</p>
          {showMergeFaqLink && onSignInMethodsHelpNavigate && (
            <SignInMethodsHelpLink
              onBeforeNavigate={onSignInMethodsHelpNavigate}
              className="w-fit font-manrope text-sm font-bold text-[#1a1a1c] underline decoration-[#1a1a1c]/30 underline-offset-2"
            />
          )}
        </div>
      )}
      <div className="flex flex-col gap-3">
        <button type="submit" className={PRIMARY_BUTTON_CLASS} disabled={isSaving}>
          {isSaving ? 'Saving…' : 'Save password'}
        </button>
        <button type="button" className={TEXT_BUTTON_CLASS} onClick={onCancel} disabled={isSaving}>
          Cancel
        </button>
      </div>
    </form>
  );
};

export default SetPasswordStep;
